import { useMemo, useState } from 'react';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ZonePhotoThumbnail } from './ZonePhotoThumbnail';
import type { Zone } from '../../types';

interface ZoneFilterSelectProps {
  value: string;
  onChange: (value: string) => void;
  zones: Zone[];
  allLabel?: string;
  className?: string;
}

interface ZoneVisualProps {
  zone: Zone;
  photoClassName: string;
  dotClassName: string;
}

function ZoneVisual({ zone, photoClassName, dotClassName }: ZoneVisualProps) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <div
        className={cn('shrink-0 rounded-full', dotClassName)}
        style={{ backgroundColor: zone.color || '#6b7280' }}
      />
      <ZonePhotoThumbnail zone={zone} className={cn('rounded-md', photoClassName)} />
      <span className="truncate">{zone.code} - {zone.name}</span>
    </div>
  );
}

export function ZoneFilterSelect({
  value,
  onChange,
  zones,
  allLabel = 'All Zones',
  className,
}: ZoneFilterSelectProps) {
  const [open, setOpen] = useState(false);

  const selectedZone = useMemo(
    () => zones.find((zone) => zone.id === value),
    [zones, value]
  );

  const handleSelect = (nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn('h-8 w-[220px] justify-between px-3 font-normal', className)}
        >
          <span className="flex min-w-0 items-center gap-2 overflow-hidden">
            {selectedZone ? (
              <ZoneVisual
                zone={selectedZone}
                photoClassName="h-[18px] w-6"
                dotClassName="h-2.5 w-2.5"
              />
            ) : (
              <span className="truncate">{allLabel}</span>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandList>
            <CommandGroup>
              <CommandItem onSelect={() => handleSelect('all')} className="py-2">
                <span className="truncate">{allLabel}</span>
                <Check
                  className={cn(
                    'ml-auto h-4 w-4 shrink-0',
                    value === 'all' ? 'opacity-100' : 'opacity-0'
                  )}
                />
              </CommandItem>
              {zones.map((zone) => (
                <CommandItem
                  key={zone.id}
                  value={zone.id}
                  onSelect={() => handleSelect(zone.id)}
                  className="py-2"
                >
                  <div className="min-w-0 flex-1">
                    <ZoneVisual
                      zone={zone}
                      photoClassName="h-8 w-10"
                      dotClassName="h-2.5 w-2.5"
                    />
                  </div>
                  <Check
                    className={cn(
                      'ml-2 h-4 w-4 shrink-0',
                      value === zone.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
